import { zodResolver } from "@hookform/resolvers/zod";
import VButton from "components/ui/VButton";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { post } from "utilities/network";
import { z } from "zod";

const getTimezoneAwareISO = (localDatetimeString: string): string => {
  const date = new Date(localDatetimeString);
  return date.toISOString();
};

const COUNTRIES = [
  { code: "GH", name: "🇬🇭 Ghana " },
  { code: "KE", name: "🇰🇪 Kenya" },
  { code: "NG", name: "🇳🇬 Nigeria" },
];

const rateSchema = z.object({
  buy: z.object({
    usd: z.number().positive("Buy rate must be positive"),
  }),
  sell: z.object({
    usd: z.number().positive("Sell rate must be positive"),
  }),
});

const bitoshiHourSchema = z
  .object({
    name: z.string().min(1, "Name is required"),
    startDateTime: z.string().min(1, "Must be a valid date & time"),
    endDateTime: z.string().min(1, "Must be a valid date & time"),
    rate: rateSchema,
    eligibleCountries: z
      .array(z.string())
      .min(1, "At least one country is required"),
  })
  .refine((data) => new Date(data.startDateTime) > new Date(), {
    message: "Start date cannot be in the past",
    path: ["startDateTime"],
  })
  .refine((data) => new Date(data.endDateTime) > new Date(), {
    message: "End date cannot be in the past",
    path: ["endDateTime"],
  })
  .refine((data) => new Date(data.startDateTime) < new Date(data.endDateTime), {
    message: "End date must be after start date",
    path: ["endDateTime"],
  });

type BitoshiHourFormData = z.infer<typeof bitoshiHourSchema>;

const getBitoshiHourSchema = (isEditMode: boolean = false) => {
  const baseSchema = z.object({
    name: z.string().min(1, "Name is required"),
    startDateTime: z.string().min(1, "Must be a valid date & time"),
    endDateTime: z.string().min(1, "Must be a valid date & time"),
    rate: rateSchema,
    eligibleCountries: z
      .array(z.string())
      .min(1, "At least one country is required"),
  });

  if (isEditMode) {
    return baseSchema.refine(
      (data) => new Date(data.startDateTime) < new Date(data.endDateTime),
      {
        message: "End date must be after start date",
        path: ["endDateTime"],
      },
    );
  }

  return baseSchema
    .refine((data) => new Date(data.startDateTime) > new Date(), {
      message: "Start date cannot be in the past",
      path: ["startDateTime"],
    })
    .refine((data) => new Date(data.endDateTime) > new Date(), {
      message: "End date cannot be in the past",
      path: ["endDateTime"],
    })
    .refine(
      (data) => new Date(data.startDateTime) < new Date(data.endDateTime),
      {
        message: "End date must be after start date",
        path: ["endDateTime"],
      },
    );
};

interface BitoshiHourFormProps {
  defaultValues?: Partial<BitoshiHourFormData>;
  isEditMode?: boolean;
  sessionId?: number;
}

export function BitoshiHourForm({
  defaultValues,
  isEditMode = false,
  sessionId,
}: BitoshiHourFormProps) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<BitoshiHourFormData>({
    resolver: zodResolver(getBitoshiHourSchema(isEditMode)),
    defaultValues: {
      eligibleCountries: [],
      rate: {
        buy: { usd: 0 },
        sell: { usd: 0 },
      },
      ...defaultValues,
    },
  });

  const handleFormSubmit = async (formData: BitoshiHourFormData) => {
    setIsLoading(true);
    try {
      if (isEditMode && sessionId) {
        const payload = {
          name: formData.name,
          bitoshi_hour_session_id: sessionId,
          startDateTime: getTimezoneAwareISO(formData.startDateTime),
          endDateTime: getTimezoneAwareISO(formData.endDateTime),
          eligibleCountries: formData.eligibleCountries,
          data: JSON.stringify({
            rate: formData.rate,
          }),
        };
        await post("admin/bitoshi-hour/update", payload);
        toast.success("Bitoshi Hour updated successfully");
      } else {
        const payload = {
          name: formData.name,
          startDateTime: getTimezoneAwareISO(formData.startDateTime),
          endDateTime: getTimezoneAwareISO(formData.endDateTime),
          eligibleCountries: formData.eligibleCountries,
          data: JSON.stringify({
            rate: formData.rate,
          }),
        };
        await post("admin/bitoshi-hour/create", payload);
        toast.success("Bitoshi Hour created successfully");
      }

      navigate("/bitoshi-hour");
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ??
          (isEditMode
            ? "Failed to update Bitoshi Hour"
            : "Failed to create Bitoshi Hour"),
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {/* Name */}
      <div>
        <label htmlFor="name" className="block text-sm font-medium">
          Event Name
        </label>
        <input
          {...register("name")}
          type="text"
          id="name"
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
          placeholder="e.g. Independence Day"
        />
        {errors.name && (
          <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
        )}
      </div>

      {/* Start & End DateTime Row */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="startDateTime" className="block text-sm font-medium">
            Start Date & Time
          </label>
          <input
            {...register("startDateTime")}
            type="datetime-local"
            id="startDateTime"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
          />
          {errors.startDateTime && (
            <p className="mt-1 text-sm text-red-600">
              {errors.startDateTime.message}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="endDateTime" className="block text-sm font-medium">
            End Date & Time
          </label>
          <input
            {...register("endDateTime")}
            type="datetime-local"
            id="endDateTime"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
          />
          {errors.endDateTime && (
            <p className="mt-1 text-sm text-red-600">
              {errors.endDateTime.message}
            </p>
          )}
        </div>
      </div>

      {/* Buy & Sell Rate Row */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="buyRate" className="block text-sm font-medium">
            Buy Rate
          </label>
          <input
            {...register("rate.buy.usd", { valueAsNumber: true })}
            type="number"
            id="buyRate"
            step="0.01"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
          />
          {errors.rate?.buy?.usd && (
            <p className="mt-1 text-sm text-red-600">
              {errors.rate.buy.usd.message}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="sellRate" className="block text-sm font-medium">
            Sell Rate
          </label>
          <input
            {...register("rate.sell.usd", { valueAsNumber: true })}
            type="number"
            id="sellRate"
            step="0.01"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
          />
          {errors.rate?.sell?.usd && (
            <p className="mt-1 text-sm text-red-600">
              {errors.rate.sell.usd.message}
            </p>
          )}
        </div>
      </div>

      {/* Eligible Countries Row */}
      <div className="grid grid-cols-2 gap-4">
        {/* Eligible Countries Checkboxes */}
        <Controller
          name="eligibleCountries"
          control={control}
          render={({ field }) => (
            <div>
              <label className="block text-sm font-medium">
                Eligible Countries
              </label>
              <div className="mt-2 space-y-2">
                {COUNTRIES.map((country) => (
                  <label key={country.code} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={field.value.includes(country.code)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          field.onChange([...field.value, country.code]);
                        } else {
                          field.onChange(
                            field.value.filter((c) => c !== country.code),
                          );
                        }
                      }}
                      className="rounded border-gray-300"
                    />
                    <span className="ml-2 text-sm">{country.name}</span>
                  </label>
                ))}
              </div>
              {errors.eligibleCountries && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.eligibleCountries.message}
                </p>
              )}
            </div>
          )}
        />
      </div>

      <VButton
        type="submit"
        status={isLoading ? "pending" : ""}
        className="py-4 px-8 text-sm font-medium text-white shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isLoading
          ? isEditMode
            ? "Updating..."
            : "Creating..."
          : isEditMode
            ? "Update Event"
            : "Create Event"}
      </VButton>
    </form>
  );
}
